// app/javascript/controllers/payment_method_controller.js
import { Controller } from "@hotwired/stimulus";

// Payment Method Tabs controller
//
// This controller manages the tabs for selecting payment methods
// and the select dropdown for existing payment methods.
//
// It listens for changes to the tabs and select dropdown and notifies other controllers
// of the selected payment method.
export default class extends Controller {
  static targets = ["select", "panel"];

  static values = {
    methodType: String, // card, bank
    activeTab: String, // existing, card, bank
  };

  connect() {
    this.disableHiddenPanels();
    // Notify of initial payment method
    this.notifyPaymentMethodChange(this.methodTypeValue);
  }

  // Actions

  // Manage the tabs for selecting payment methods
  handleTabChange(event) {
    const tab = event.target; //event.detail.tab

    this.methodTypeValue = this.getPaymentMethodForTab(tab);
    this.activeTabValue = tab.dataset.methodType;

    this.tabShown(event); // enable/disable inputs
    this.notifyPaymentMethodChange(this.methodTypeValue); // notify other controllers
  }

  // Manage the select dropdown for existing payment methods
  handleSelectChange(event) {
    const select = event.target; //event.detail.select
    const methodType = select.selectedOptions[0].dataset.paymentMethod;

    this.methodTypeValue = methodType;
    this.notifyPaymentMethodChange(methodType);
  }

  // private

  getPaymentMethodForTab(tab) {
    switch (tab.dataset.methodType) {
      case "existing":
        return this.hasSelectTarget
          ? this.selectTarget.selectedOptions[0].dataset.paymentMethod
          : "credit_card";
      case "credit_card":
        return "credit_card";
      case "bank_account":
        return "bank_account";
      default:
        console.error("Unknown payment method type");
        return "credit_card";
    }
  }

  notifyPaymentMethodChange(method) {
    const event = new CustomEvent("payment-method:changed", {
      detail: { method },
      bubbles: true,
    });
    this.element.dispatchEvent(event);
  }

  tabShown(event) {
    const tab = event.target || event.detail?.tab;
    const targetId = tab.getAttribute("href")?.replace("#", "");
    const currentPanel = document.getElementById(targetId);

    // Disable inputs in all other panels, enable in current
    this.panelTargets.forEach((panel) => {
      this.setInputsDisabled(panel, panel !== currentPanel);
    });
  }

  // Disable all inputs in inactive panels initially
  disableHiddenPanels() {
    this.panelTargets.forEach((panel) => {
      if (!panel.classList.contains("active")) {
        this.setInputsDisabled(panel, true);
      }
    });
  }

  // Helper method to enable/disable all inputs within a panel
  // Reason: when a tab is hidden, we want to disable all inputs to prevent
  // form submission or user interaction with hidden fields.
  setInputsDisabled(panel, disabled) {
    if (!panel) return;

    panel
      .querySelectorAll("input, select, textarea, button")
      .forEach((input) => (input.disabled = disabled));
  }
}
